/**
 * data/repos/recordingsRepo — repositório da área `recordings` e `context`
 * (STORY-06, TASK-34).
 *
 * Resolve a fonte via `data/source.ts`:
 * - área real (`VITE_API_REAL_AREAS` contém `recordings`/`context`, ou produção)
 *   → chamadas ao azim-api (/v1/recordings, /v1/context).
 * - caso contrário → mock tipado (`data/mock.ts`), sem chamada de rede.
 *
 * Contratos do backend:
 * - `GET /v1/recordings/by-subject?subjectType=&subjectId=&page=&pageSize=`
 *   → `RecordingListResult` (camelCase)
 * - `GET /v1/recordings/{id}/transcript`
 *   → `TranscriptResponse` (camelCase)
 * - `GET /v1/context?subject_type=&subject_id=&q=&k=`
 *   → array de objetos snake_case `[{ recording_id, chunk_text, score }]`
 * - `GET /v1/recordings/desktop-client/latest?platform=`
 *   → `DesktopClientReleaseDto` (camelCase); 404 = sem release publicada para a
 *   plataforma (tratado como ausência, não como erro — ver getDesktopClientRelease)
 *
 * Todos os DTOs privados ficam neste arquivo (ponto único de tradução).
 * As interfaces públicas exportadas são em camelCase (padrão do portal).
 */

import { apiClient, ApiError } from "../../services/ApiClient.js";
import { resolveSource } from "../source.js";
import type {
  RecordingListItem,
  RecordingPage,
  RecordingStatus,
  SummaryStatus,
  Transcript,
  ContextChunk,
  SpeakerMapping,
  RecordingParticipant,
  UpdateRecordingMetaInput,
  GlossaryItem,
  ReplaceTermInput,
  RecordingGlossaryEntry,
  RecordingGlossary,
  PendingRecordingItem,
  PendingRecordingsPage,
  ClassifySubjectType,
  DesktopClientPlatform,
  DesktopClientRelease,
} from "../../features/call-recordings/types.js";

// ---------------------------------------------------------------------------
// DTOs privados — wire format do backend
// ---------------------------------------------------------------------------

/** DTO de um item de gravação retornado pelo `GET /v1/recordings/by-subject`. */
interface RecordingItemDto {
  id: string;
  /** Nome canônico no formato `yyyy.MM.dd.{id}`. Campo obrigatório na resposta do backend. */
  name?: string | null;
  tenantId: string;
  subjectType: string;
  subjectId: string;
  status: string;
  storageUri?: string | null;
  consentReference: string;
  language?: string | null;
  noSpeech?: boolean | null;
  failureReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** DTO da resposta paginada de `GET /v1/recordings/by-subject`. */
interface RecordingListResultDto {
  items: RecordingItemDto[];
  total: number;
  page: number;
  pageSize: number;
}

/** DTO de segmento de transcrição (camelCase, conforme contrato). */
interface TranscriptSegmentDto {
  sequence: number;
  speaker: string;
  start: number;
  end: number;
  text: string;
}

/** DTO de participante declarado na transcrição. */
interface ParticipantDto {
  name: string;
  role?: string | null;
  speakerLabel?: string | null;
}

/** DTO de mapeamento speaker → displayName na transcrição. */
interface SpeakerDto {
  speaker: string;
  displayName: string;
}

/** DTO da resposta de `GET /v1/recordings/{id}/transcript`. */
interface TranscriptResponseDto {
  recordingId: string;
  /** Nome canônico no formato `yyyy.MM.dd.{recordingId}`. Campo obrigatório na resposta do backend. */
  name?: string | null;
  status: string;
  language?: string | null;
  noSpeech?: boolean | null;
  segments: TranscriptSegmentDto[];
  audioUrl?: string | null;
  audioUrlExpiresAt?: string | null;
  /** Título da reunião (campo editável, opcional). */
  title?: string | null;
  /** Local da reunião (campo editável, opcional). */
  meetingLocation?: string | null;
  /** Participantes declarados (campo editável, opcional). */
  participants?: ParticipantDto[] | null;
  /** Mapeamentos speaker → displayName (editável, opcional). Backend: `speakerMappings`. */
  speakerMappings?: SpeakerDto[] | null;
  /** Resumo gerado automaticamente da transcrição (opcional). */
  summary?: string | null;
  /** Status da geração do resumo (camelCase, minúsculo). */
  summaryStatus?: string | null;
  /** Mensagem de erro da última tentativa de geração. */
  summaryError?: string | null;
  /** Data/hora ISO 8601 UTC em que o resumo foi gerado. */
  summaryGeneratedAt?: string | null;
}

/** DTO de um item do glossário. */
interface GlossaryItemDto {
  id: string;
  term: string;
  replacement: string;
}

/** DTO da resposta de `GET /v1/glossary`. */
interface GlossaryResponseDto {
  items: GlossaryItemDto[];
}

/** DTO de uma entrada do glossário híbrido por gravação. */
interface RecordingGlossaryEntryDto {
  id: string;
  term: string;
  replacement: string;
  accountId: string | null;
}

/** DTO da resposta de `GET /v1/recordings/{id}/glossary`. */
interface RecordingGlossaryDto {
  accountId: string | null;
  global: RecordingGlossaryEntryDto[];
  account: RecordingGlossaryEntryDto[];
}

/**
 * DTO de um trecho de contexto RAG retornado por `GET /v1/context`.
 * O backend usa snake_case para os query params e para o corpo da resposta.
 */
interface ContextChunkDto {
  recording_id: string;
  chunk_text: string;
  score: number;
}

/** DTO de uma gravação pendente de classificação (`GET /v1/recordings/pending`). */
interface PendingRecordingItemDto {
  id: string;
  name: string;
  createdAt: string;
  durationSeconds?: number | null;
  /** Sempre "awaiting_classification" para este endpoint. */
  status: string;
  /** URL assinada de leitura do áudio — presente quando upload foi concluído. */
  audioUrl?: string | null;
  /** Data de expiração da URL assinada (ISO 8601 UTC). */
  audioUrlExpiresAt?: string | null;
}

/** DTO da resposta paginada de `GET /v1/recordings/pending`. */
interface PendingRecordingPageDto {
  items: PendingRecordingItemDto[];
  total: number;
  page: number;
  pageSize: number;
}

/** DTO de `GET /v1/recordings/desktop-client/latest?platform=` (200 — release existe). */
interface DesktopClientReleaseDto {
  platform: string;
  version: string;
  sizeBytes: number;
  sha256: string;
  releasedAt: string;
  downloadUrl: string;
  expiresAt: string;
}

// ---------------------------------------------------------------------------
// Parâmetros públicos
// ---------------------------------------------------------------------------

/** Parâmetros para listar gravações por subject. */
export interface ListRecordingsBySubjectParams {
  readonly subjectType: string;
  readonly subjectId: string;
  readonly page?: number;
  readonly pageSize?: number;
}

/** Parâmetros para busca de trechos RAG de contexto. */
export interface GetContextParams {
  readonly subjectType: string;
  readonly subjectId: string;
  readonly q?: string;
  readonly k?: number;
}

/** Parâmetros para listar gravações aguardando classificação. */
export interface ListPendingRecordingsParams {
  readonly page?: number;
  readonly pageSize?: number;
}

/** Input para classificar uma gravação vinculando-a a um subject. */
export interface ClassifyRecordingInput {
  readonly id: string;
  readonly subjectType: ClassifySubjectType;
  readonly subjectId: string;
}

/** Parâmetros para obter a última release do Azim Recorder de uma plataforma. */
export interface GetDesktopClientReleaseParams {
  readonly platform: DesktopClientPlatform;
}

// ---------------------------------------------------------------------------
// Mapeadores internos (DTO → portal)
// ---------------------------------------------------------------------------

/**
 * Normaliza o status para o vocabulário do portal (`RecordingStatus`).
 *
 * O backend `call-recordings` persiste/serializa o **nome do enum** de domínio
 * (`Uploaded`/`Transcribing`/`Transcribed`/`Failed`, PascalCase) e trata a ausência
 * de fala como um booleano separado (`noSpeech`). O portal usa um vocabulário próprio
 * em lowercase (`pending`/`processing`/`transcribed`/`failed`/`no_speech`). Este é o
 * ponto de tradução dessa divergência de contrato (mocks já usam o vocabulário do portal).
 */
function normalizeStatus(raw: string, noSpeech?: boolean | null): RecordingStatus {
  if (noSpeech === true) return "no_speech";
  switch (raw.toLowerCase()) {
    case "uploaded":
    case "pending":
      return "pending";
    case "transcribing":
    case "processing":
      return "processing";
    case "transcribed":
      return "transcribed";
    case "revised":
      return "revised";
    case "failed":
      return "failed";
    case "no_speech":
      return "no_speech";
    default:
      return "pending";
  }
}

/**
 * Normaliza o summaryStatus vindo do backend para o vocabulário do portal.
 * Retorna `undefined` quando o campo está ausente ou contém valor desconhecido.
 */
function normalizeSummaryStatus(raw: string | null | undefined): SummaryStatus | undefined {
  if (raw == null) return undefined;
  const lower = raw.toLowerCase();
  if (
    lower === "none" ||
    lower === "pending" ||
    lower === "generating" ||
    lower === "ready" ||
    lower === "failed"
  ) {
    return lower as SummaryStatus;
  }
  return undefined;
}

function toRecordingListItem(dto: RecordingItemDto): RecordingListItem {
  // Campos opcionais são incluídos apenas quando presentes (exactOptionalPropertyTypes):
  // atribuir `undefined` explícito violaria o tipo — usamos conditional spread.
  return {
    id: dto.id,
    // Fallback defensivo: se o backend ainda não enviou `name`, usa o id.
    name: dto.name ?? dto.id,
    tenantId: dto.tenantId,
    subjectType: dto.subjectType,
    subjectId: dto.subjectId,
    status: normalizeStatus(dto.status, dto.noSpeech),
    consentReference: dto.consentReference,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    ...(dto.storageUri != null ? { storageUri: dto.storageUri } : {}),
    ...(dto.language != null ? { language: dto.language } : {}),
    ...(dto.noSpeech != null ? { noSpeech: dto.noSpeech } : {}),
    ...(dto.failureReason != null ? { failureReason: dto.failureReason } : {}),
  };
}

function toTranscript(dto: TranscriptResponseDto): Transcript {
  const mappedParticipants: RecordingParticipant[] | undefined =
    dto.participants != null && dto.participants.length > 0
      ? dto.participants.map((p) => ({
          name: p.name,
          ...(p.role != null ? { role: p.role } : {}),
          ...(p.speakerLabel != null ? { speakerLabel: p.speakerLabel } : {}),
        }))
      : undefined;

  const mappedSpeakers: SpeakerMapping[] | undefined =
    dto.speakerMappings != null && dto.speakerMappings.length > 0
      ? dto.speakerMappings.map((s) => ({ speaker: s.speaker, displayName: s.displayName }))
      : undefined;

  const summaryStatus = normalizeSummaryStatus(dto.summaryStatus);

  return {
    recordingId: dto.recordingId,
    // Fallback defensivo: se o backend ainda não enviou `name`, usa o recordingId.
    name: dto.name ?? dto.recordingId,
    status: normalizeStatus(dto.status, dto.noSpeech),
    segments: dto.segments.map((s) => ({
      sequence: s.sequence,
      speaker: s.speaker,
      start: s.start,
      end: s.end,
      text: s.text,
    })),
    ...(dto.language != null ? { language: dto.language } : {}),
    ...(dto.noSpeech != null ? { noSpeech: dto.noSpeech } : {}),
    ...(dto.audioUrl != null ? { audioUrl: dto.audioUrl } : {}),
    ...(dto.audioUrlExpiresAt != null ? { audioUrlExpiresAt: dto.audioUrlExpiresAt } : {}),
    ...(dto.title != null ? { title: dto.title } : {}),
    ...(dto.meetingLocation != null ? { meetingLocation: dto.meetingLocation } : {}),
    ...(mappedParticipants !== undefined ? { participants: mappedParticipants } : {}),
    ...(mappedSpeakers !== undefined ? { speakers: mappedSpeakers } : {}),
    ...(dto.summary != null ? { summary: dto.summary } : {}),
    ...(summaryStatus !== undefined ? { summaryStatus } : {}),
    ...(dto.summaryError != null ? { summaryError: dto.summaryError } : {}),
    ...(dto.summaryGeneratedAt != null ? { summaryGeneratedAt: dto.summaryGeneratedAt } : {}),
  };
}

function toContextChunk(dto: ContextChunkDto): ContextChunk {
  return {
    recordingId: dto.recording_id,
    chunkText: dto.chunk_text,
    score: dto.score,
  };
}

function toPendingRecordingItem(dto: PendingRecordingItemDto): PendingRecordingItem {
  return {
    id: dto.id,
    name: dto.name ?? dto.id,
    createdAt: dto.createdAt,
    status: "awaiting_classification",
    ...(dto.durationSeconds != null ? { durationSeconds: dto.durationSeconds } : {}),
    ...(dto.audioUrl != null ? { audioUrl: dto.audioUrl } : {}),
    ...(dto.audioUrlExpiresAt != null ? { audioUrlExpiresAt: dto.audioUrlExpiresAt } : {}),
  };
}

function toRecordingGlossaryEntry(dto: RecordingGlossaryEntryDto): RecordingGlossaryEntry {
  return {
    id: dto.id,
    term: dto.term,
    replacement: dto.replacement,
    accountId: dto.accountId,
  };
}

function toDesktopClientRelease(dto: DesktopClientReleaseDto): DesktopClientRelease {
  return {
    platform: dto.platform as DesktopClientPlatform,
    version: dto.version,
    sizeBytes: dto.sizeBytes,
    sha256: dto.sha256,
    releasedAt: dto.releasedAt,
    downloadUrl: dto.downloadUrl,
    expiresAt: dto.expiresAt,
  };
}

// ---------------------------------------------------------------------------
// Funções exportadas — leitura
// ---------------------------------------------------------------------------

/**
 * Lista gravações por subject (oportunidade ou parceiro).
 * Real → `GET /v1/recordings/by-subject`; mock → `data/mock.ts`.
 */
export async function listRecordingsBySubject(
  params: ListRecordingsBySubjectParams,
): Promise<RecordingPage> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    const query: Record<string, string | number> = {
      subjectType: params.subjectType,
      subjectId: params.subjectId,
    };
    if (params.page !== undefined) query["page"] = params.page;
    if (params.pageSize !== undefined) query["pageSize"] = params.pageSize;

    const dto = await apiClient.get<RecordingListResultDto>(
      "/recordings/by-subject",
      query,
    );
    return {
      items: (dto.items ?? []).map(toRecordingListItem),
      total: dto.total ?? 0,
      page: dto.page ?? 1,
      pageSize: dto.pageSize ?? (dto.items?.length ?? 0),
    };
  }

  // Caminho mock: filtra por subjectType e subjectId.
  const mock = source.mock;
  if (!mock) throw new Error("fonte mock indisponível");
  const all = mock.recordings.filter(
    (r) =>
      r.subjectType === params.subjectType && r.subjectId === params.subjectId,
  );
  const page = Math.max(1, params.page ?? 1);
  const pageSize = Math.max(1, params.pageSize ?? (all.length || 10));
  const start = (page - 1) * pageSize;

  return {
    items: all.slice(start, start + pageSize).map(toRecordingListItem),
    total: all.length,
    page,
    pageSize,
  };
}

/**
 * Obtém a transcrição diarizada de uma gravação.
 * Real → `GET /v1/recordings/{id}/transcript`; mock → `data/mock.ts`.
 */
export async function getTranscript(recordingId: string): Promise<Transcript> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    const dto = await apiClient.get<TranscriptResponseDto>(
      `/recordings/${recordingId}/transcript`,
    );
    return toTranscript(dto);
  }

  // Caminho mock.
  const mock = source.mock;
  if (!mock) throw new Error("fonte mock indisponível");
  const t = mock.transcripts[recordingId];
  if (!t) {
    return {
      recordingId,
      name: recordingId,
      status: "pending",
      summaryStatus: "none",
      segments: [],
    };
  }
  // No mock, summaryStatus nunca vem do JSON → garante "none" para UX consistente.
  const mapped = toTranscript(t);
  return {
    ...mapped,
    summaryStatus: (mapped.summaryStatus ?? "none") as SummaryStatus,
  };
}

// ---------------------------------------------------------------------------
// Funções exportadas — escrita (mutações)
// ---------------------------------------------------------------------------

/**
 * Atualiza metadados editáveis de uma gravação (título, local, participantes).
 * Real → `PATCH /v1/recordings/{id}` (body camelCase); mock → no-op.
 */
export async function updateRecordingMeta(
  recordingId: string,
  input: UpdateRecordingMetaInput,
): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    const body: Record<string, unknown> = {};
    if (input.title !== undefined) body["title"] = input.title;
    if (input.meetingLocation !== undefined) body["meetingLocation"] = input.meetingLocation;
    if (input.participants !== undefined) body["participants"] = input.participants;
    await apiClient.patch<void>(`/recordings/${recordingId}`, body);
    return;
  }

  // Modo mock: operação de escrita é no-op (dados em memória não persistem).
}

/**
 * Atualiza os nomes de exibição dos speakers de uma gravação.
 * Real → `PATCH /v1/recordings/{id}/speakers` (body camelCase); mock → no-op.
 */
export async function renameSpeakers(
  recordingId: string,
  mappings: readonly SpeakerMapping[],
): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    await apiClient.patch<void>(`/recordings/${recordingId}/speakers`, { mappings });
    return;
  }
  // Modo mock: no-op.
}

/**
 * Atualiza o texto de um segmento específico da transcrição.
 * Real → `PATCH /v1/recordings/{id}/transcript/segments/{sequence}`; mock → no-op.
 */
export async function updateSegmentText(
  recordingId: string,
  sequence: number,
  text: string,
): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    await apiClient.patch<void>(
      `/recordings/${recordingId}/transcript/segments/${sequence}`,
      { text },
    );
    return;
  }
  // Modo mock: no-op.
}

/**
 * Marca uma gravação como revisada (status → "Revised").
 * Real → `POST /v1/recordings/{id}/revised` (body vazio, resposta 204); mock → no-op.
 */
export async function markRevised(recordingId: string): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    await apiClient.post<void>(`/recordings/${recordingId}/revised`, {});
    return;
  }
  // Modo mock: no-op.
}

/**
 * Substitui um termo na transcrição de uma gravação.
 * Real → `POST /v1/recordings/{id}/transcript/replace` (body camelCase, resposta 204); mock → no-op.
 */
export async function replaceTerm(
  recordingId: string,
  input: ReplaceTermInput,
): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    await apiClient.post<void>(`/recordings/${recordingId}/transcript/replace`, {
      find: input.find,
      replace: input.replace,
      addToGlossary: input.addToGlossary,
    });
    return;
  }
  // Modo mock: no-op.
}

/**
 * Lista todos os itens do glossário de substituição de termos.
 * Real → `GET /v1/glossary`; mock → lista vazia.
 */
export async function listGlossary(): Promise<GlossaryItem[]> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    const dto = await apiClient.get<GlossaryResponseDto>("/glossary");
    return (dto.items ?? []).map((item) => ({
      id: item.id,
      term: item.term,
      replacement: item.replacement,
    }));
  }
  // Modo mock: lista vazia.
  return [];
}

/**
 * Adiciona um item ao glossário de substituição de termos.
 * Real → `POST /v1/glossary` → `{id, term, replacement}`; mock → item com id fictício.
 */
export async function addGlossary(item: {
  term: string;
  replacement: string;
}): Promise<GlossaryItem> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    const dto = await apiClient.post<GlossaryItemDto>("/glossary", {
      term: item.term,
      replacement: item.replacement,
    });
    return { id: dto.id, term: dto.term, replacement: dto.replacement };
  }
  // Modo mock: retorna item com id fictício.
  return { id: `mock-${Date.now()}`, term: item.term, replacement: item.replacement };
}

/**
 * Remove um item do glossário.
 * Real → `DELETE /v1/glossary/{id}` → 204; mock → no-op.
 */
export async function removeGlossary(id: string): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    await apiClient.delete<void>(`/glossary/${id}`);
    return;
  }
  // Modo mock: no-op.
}

/**
 * Edita uma entrada do glossário (correção de digitação do termo/substituição).
 * O escopo (global/conta) é preservado pelo backend. Vale para termos globais e de conta.
 * Real → `PATCH /v1/glossary/{id}`; mock → eco do payload.
 */
export async function updateGlossaryEntry(input: {
  id: string;
  term: string;
  replacement: string;
}): Promise<RecordingGlossaryEntry> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    const dto = await apiClient.patch<RecordingGlossaryEntryDto>(`/glossary/${input.id}`, {
      term: input.term,
      replacement: input.replacement,
    });
    return toRecordingGlossaryEntry(dto);
  }
  // Modo mock: eco do payload (escopo global no mock).
  return { id: input.id, term: input.term, replacement: input.replacement, accountId: null };
}

/**
 * Edita manualmente o texto do resumo de uma gravação.
 * Real → `PATCH /v1/recordings/{id}/summary` com `{ text }` (204 em sucesso;
 *         409 se não houver resumo Ready; 400 se texto vazio); mock → no-op.
 */
export async function updateSummary(recordingId: string, text: string): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    await apiClient.patch<void>(`/recordings/${recordingId}/summary`, { text });
    return;
  }
  // Modo mock: no-op.
}

/**
 * Solicita a geração assíncrona do resumo de uma gravação via LLM.
 * Real → `POST /v1/recordings/{id}/summary` (body vazio).
 *   - 2xx → solicitação aceita.
 *   - 409 → já em geração, idempotente (trata como sucesso).
 *   - outros erros → lança exceção (propagada ao mutation hook).
 * Mock → no-op.
 */
export async function generateSummary(recordingId: string): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    try {
      await apiClient.post<void>(`/recordings/${recordingId}/summary`, {});
    } catch (err) {
      // 409 = já em geração; tratar como sucesso idempotente
      if (err instanceof ApiError && err.status === 409) return;
      throw err;
    }
    return;
  }
  // Modo mock: no-op (geração acontece fora do frontend)
}

/**
 * Obtém o glossário híbrido (global + conta) de uma gravação.
 * Real → `GET /v1/recordings/{id}/glossary`; mock → termos fictícios.
 */
export async function getRecordingGlossary(recordingId: string): Promise<RecordingGlossary> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    const dto = await apiClient.get<RecordingGlossaryDto>(
      `/recordings/${recordingId}/glossary`,
    );
    return {
      accountId: dto.accountId,
      global: (dto.global ?? []).map(toRecordingGlossaryEntry),
      account: (dto.account ?? []).map(toRecordingGlossaryEntry),
    };
  }

  // Modo mock: alguns termos globais fictícios; gravações de mock não têm conta.
  return {
    accountId: null,
    global: [
      { id: "mock-g1", term: "CRM", replacement: "Customer Relationship Management", accountId: null },
      { id: "mock-g2", term: "SLA", replacement: "Service Level Agreement", accountId: null },
      { id: "mock-g3", term: "NDA", replacement: "Non-Disclosure Agreement", accountId: null },
    ],
    account: [],
  };
}

// ---------------------------------------------------------------------------
// Funções exportadas — inbox de gravações pendentes (Parte B)
// ---------------------------------------------------------------------------

/**
 * Lista gravações aguardando classificação do usuário logado.
 * Real → `GET /v1/recordings/pending?page=&pageSize=`; mock → `data/mock.ts`.
 *
 * Erro 404 não se aplica (endpoint sempre retorna página, possivelmente vazia).
 */
export async function listPendingRecordings(
  params: ListPendingRecordingsParams = {},
): Promise<PendingRecordingsPage> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    const query: Record<string, number> = {};
    if (params.page !== undefined) query["page"] = params.page;
    if (params.pageSize !== undefined) query["pageSize"] = params.pageSize;

    const dto = await apiClient.get<PendingRecordingPageDto>(
      "/recordings/pending",
      Object.keys(query).length > 0 ? query : undefined,
    );
    return {
      items: (dto.items ?? []).map(toPendingRecordingItem),
      total: dto.total ?? 0,
      page: dto.page ?? 1,
      pageSize: dto.pageSize ?? (dto.items?.length ?? 0),
    };
  }

  // Caminho mock.
  const mock = source.mock;
  if (!mock) throw new Error("fonte mock indisponível");

  const all = mock.pendingRecordings;
  const page = Math.max(1, params.page ?? 1);
  const pageSize = Math.max(1, params.pageSize ?? (all.length || 10));
  const start = (page - 1) * pageSize;

  return {
    items: all.slice(start, start + pageSize).map(toPendingRecordingItem),
    total: all.length,
    page,
    pageSize,
  };
}

/**
 * Classifica uma gravação vinculando-a a um subject (oportunidade ou parceiro).
 * Real → `POST /v1/recordings/{id}/classify` com body `{ subjectType, subjectId }`.
 *
 * Erros tratados:
 * - 404 → gravação não encontrada ou não pertence ao usuário logado.
 * - 409 → gravação já classificada (idempotente — trata como sucesso).
 * Outros erros são propagados ao hook chamador.
 *
 * Mock → no-op (a lista é recarregada via invalidação do React Query).
 */
export async function classifyRecording(input: ClassifyRecordingInput): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    try {
      await apiClient.post<void>(`/recordings/${input.id}/classify`, {
        subjectType: input.subjectType,
        subjectId: input.subjectId,
      });
    } catch (err) {
      // 409 = já classificada; tratamos como sucesso idempotente.
      if (err instanceof ApiError && err.status === 409) return;
      throw err;
    }
    return;
  }
  // Mock: no-op — a invalidação do React Query dispara novo fetch do mock.
}

/**
 * Apaga uma gravação pendente (aguardando classificação).
 * Real → `DELETE /v1/recordings/{id}`.
 *
 * Erros tratados:
 * - 404 → gravação já removida ou inexistente (idempotente — trata como sucesso).
 * Outros erros são propagados ao hook chamador.
 *
 * Mock → no-op (a lista é recarregada via invalidação do React Query).
 */
export async function deletePendingRecording(id: string): Promise<void> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    try {
      await apiClient.delete<void>(`/recordings/${id}`);
    } catch (err) {
      // 404 = já removida; tratamos como sucesso idempotente.
      if (err instanceof ApiError && err.status === 404) return;
      throw err;
    }
    return;
  }
  // Mock: no-op — a invalidação do React Query dispara novo fetch do mock.
}

/**
 * Obtém trechos de contexto RAG para um subject.
 * Real → `GET /v1/context` (query params em snake_case); mock → `data/mock.ts`.
 */
export async function getContext(params: GetContextParams): Promise<ContextChunk[]> {
  const source = await resolveSource("context");

  if (source.kind === "api") {
    const query: Record<string, string | number> = {
      subject_type: params.subjectType,
      subject_id: params.subjectId,
    };
    if (params.q !== undefined && params.q !== "") query["q"] = params.q;
    if (params.k !== undefined) query["k"] = params.k;

    const dto = await apiClient.get<ContextChunkDto[]>("/context", query);
    return (dto ?? []).map(toContextChunk);
  }

  // Caminho mock: usa chave "subjectType:subjectId".
  const mock = source.mock;
  if (!mock) throw new Error("fonte mock indisponível");
  const key = `${params.subjectType}:${params.subjectId}`;
  const chunks = (mock.contextChunks as Record<string, { recordingId: string; chunkText: string; score: number }[]>)[key] ?? [];

  // Filtra por `q` quando fornecido (busca simples em dev).
  const q = params.q?.toLowerCase();
  const filtered = q
    ? chunks.filter((c) => c.chunkText.toLowerCase().includes(q))
    : chunks;

  const k = params.k ?? filtered.length;
  return filtered.slice(0, k).map((c) => ({
    recordingId: c.recordingId,
    chunkText: c.chunkText,
    score: c.score,
  }));
}

// ---------------------------------------------------------------------------
// Funções exportadas — distribuição do Azim Recorder (cliente desktop)
// ---------------------------------------------------------------------------

/**
 * Obtém a última release publicada do Azim Recorder para uma plataforma.
 * Real → `GET /v1/recordings/desktop-client/latest?platform=`; mock → `data/mock.ts`.
 *
 * Retorna `null` — em vez de lançar — quando o backend responde 404: significa
 * que ainda não há release publicada para a plataforma pedida, um estado válido
 * do domínio (nem toda plataforma tem build disponível o tempo todo), não uma
 * falha de rede. O chamador (hook) distingue os dois casos: `null` é
 * "vazio conhecido", exceção é "algo deu errado".
 */
export async function getDesktopClientRelease(
  params: GetDesktopClientReleaseParams,
): Promise<DesktopClientRelease | null> {
  const source = await resolveSource("recordings");

  if (source.kind === "api") {
    try {
      const dto = await apiClient.get<DesktopClientReleaseDto>(
        "/recordings/desktop-client/latest",
        { platform: params.platform },
      );
      return toDesktopClientRelease(dto);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  }

  // Caminho mock.
  const mock = source.mock;
  if (!mock) throw new Error("fonte mock indisponível");
  const release = mock.desktopClientReleases[params.platform];
  return release ? { ...release, platform: params.platform } : null;
}
